
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface WalletTransferDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  currentBalance: number;
  transferToId: string;
  transferToName: string;
}

const WalletTransferDialog: React.FC<WalletTransferDialogProps> = ({
  open,
  onClose,
  onSuccess,
  currentBalance,
  transferToId,
  transferToName,
}) => {
  const { toast } = useToast();
  const [amount, setAmount] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [pendingInvoices, setPendingInvoices] = useState<number>(0);
  const [pendingInvoicesTotal, setPendingInvoicesTotal] = useState<number>(0);
  const [autoChargePending, setAutoChargePending] = useState<boolean>(false);
  const [maintainMinimumBalance, setMaintainMinimumBalance] = useState<boolean>(false);
  const [minimumBalance, setMinimumBalance] = useState<string>('25');

  useEffect(() => {
    // Fetch pending invoices count and total
    const fetchPendingInvoices = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('tremendous', {
          body: {
            action: 'getPendingInvoicesTotal',
          }
        });

        if (error) throw new Error(error.message);
        
        if (data) {
          setPendingInvoices(data.count || 0);
          setPendingInvoicesTotal(data.total || 0);
          
          if (data.count > 0) {
            // Set default amount to pending invoices total if there are any
            setAmount(data.total.toFixed(2));
          }
        }
      } catch (error) {
        console.error('Error fetching pending invoices:', error);
      }
    };

    if (open) {
      fetchPendingInvoices();
    }
  }, [open]);

  const handleTransfer = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast({
        title: "Invalid Amount",
        description: "Please enter a valid transfer amount",
        variant: "destructive"
      });
      return;
    }

    if (parseFloat(amount) > currentBalance) {
      toast({
        title: "Insufficient Balance",
        description: "Transfer amount exceeds your current wallet balance",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('tremendous', {
        body: {
          action: 'transferWalletFunds',
          amount: parseFloat(amount),
          toId: transferToId,
          autoChargePending,
          maintainMinimumBalance: maintainMinimumBalance ? parseFloat(minimumBalance) : null
        }
      });

      if (error) throw new Error(error.message);
      
      toast({
        title: "Transfer Complete",
        description: `Successfully transferred $${amount} to ${transferToName}`
      });
      
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error during transfer:', error);
      toast({
        title: "Transfer Failed",
        description: "Unable to complete the transfer. Please try again later.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Transfer Wallet Funds</DialogTitle>
          <DialogDescription>
            Transfer funds from your reward wallet to the {transferToName}.
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="current-balance" className="text-right">
              Current Balance
            </Label>
            <div className="col-span-3 font-medium">
              ${currentBalance.toFixed(2)}
            </div>
          </div>
          
          {pendingInvoices > 0 && (
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="pending-invoices" className="text-right">
                Pending Invoices
              </Label>
              <div className="col-span-3">
                {pendingInvoices} invoice(s) for ${pendingInvoicesTotal.toFixed(2)}
              </div>
            </div>
          )}
          
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="amount" className="text-right">
              Amount
            </Label>
            <Input
              id="amount"
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Enter transfer amount"
              className="col-span-3"
            />
          </div>
          
          {pendingInvoices > 0 && (
            <div className="grid grid-cols-4 items-center gap-4">
              <div className="col-span-4 flex items-center space-x-2">
                <Checkbox
                  id="auto-charge-pending"
                  checked={autoChargePending}
                  onCheckedChange={(checked) => {
                    setAutoChargePending(checked === true);
                    if (checked) {
                      setAmount(pendingInvoicesTotal.toFixed(2));
                    }
                  }}
                />
                <Label htmlFor="auto-charge-pending">
                  Automatically charge for all pending invoices (${pendingInvoicesTotal.toFixed(2)})
                </Label>
              </div>
            </div>
          )}
          
          <div className="grid grid-cols-4 items-center gap-4">
            <div className="col-span-4 flex items-center space-x-2">
              <Switch
                id="minimum-balance"
                checked={maintainMinimumBalance}
                onCheckedChange={(checked) => setMaintainMinimumBalance(checked)}
              />
              <Label htmlFor="minimum-balance">
                Maintain minimum wallet balance
              </Label>
            </div>
          </div>
          
          {maintainMinimumBalance && (
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="min-amount" className="text-right">
                Minimum Balance ($)
              </Label>
              <Input
                id="min-amount"
                type="number"
                step="0.01"
                value={minimumBalance}
                onChange={(e) => setMinimumBalance(e.target.value)}
                className="col-span-3"
              />
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleTransfer} disabled={loading}>
            {loading ? 'Processing...' : 'Transfer Funds'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WalletTransferDialog;
